import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter } from "react-router";
import { LENDER_REPORTING_DASHBOARD } from "constants/routes";
import { getContactsByCompany } from "store/state/contacts";
import { usePrevious } from "helpers/use-previous";
import { v4 as uuidv4 } from "uuid";
import styled from "styled-components";

import { getPermissionedUsers, updatePermissionedUsers, getPermissionedUser } from "../lenderReportingDashboard.store";
import { LrdCheckType, LrdRoleType } from "../constants";

import { SettingsPageHeader, SettingsModule } from "./";

export const WIDTH = "986px";

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const UnauthorizedSection = styled.div`
    margin-top: 5em;
    font-family: Work Sans;
    font-size: 18px;
    font-weight: 400;
    line-height: 21px;
    text-align: center;
`;

const LenderReportingSettings = ({
  history,
  getContactsByCompany,
  contacts,
  getPermissionedUser,
  permissionedUser,
  getPermissionedUsers,
  permissionedUsers,
  updatePermissionedUsers,
  authentication
}) => {
  const [contactsState, setContactsState] = useState([]);
  const [permissionedUsersState, setPermissionedUsersState] = useState([]);
  const [originalPermissionedUsers, setOriginalPermissionedUsers] = useState(permissionedUsers);
  const [selectedUser, setSelectedUser] = useState({ id: "", name: "" });
  const [deletedUserIds, setDeletedUserIds] = useState([]);
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [isLrdAdmin, setIsLrdAdmin] = useState(false);

  const prevPermissionedUsers = usePrevious(permissionedUsers);
  const flatContacts = contacts ? Object.values(contacts).flatMap(contact => contact) : [];

  useEffect(() => {
    getContactsByCompany();
  }, []);

  useEffect(() => {
    getPermissionedUser({ contactId: authentication.user.contact.id });
  }, [authentication]);

  useEffect(() => {
    setIsSystemAdmin(authentication?.isSystemAdmin);
    setIsLrdAdmin(permissionedUser?.lenderReportingRoleTypeId === LrdRoleType.ADMIN);
  }, [permissionedUser, authentication]);

  useEffect(() => {
    if (!prevPermissionedUsers || prevPermissionedUsers.length === 0 && permissionedUsers.length !== 0) {
      setOriginalPermissionedUsers(permissionedUsers);
    }
    setPermissionedUsersState(permissionedUsers);
  }, [permissionedUsers]);

  useEffect(() => {
    resetUserOptions(permissionedUsersState);
  }, [permissionedUsersState]);

  useEffect(() => {
    if (contacts) {
      const userOptions = flatContacts.map(({ name, id }) => ({ name, id }));
      setContactsState(userOptions);

      const contactIds = flatContacts.map(({ id }) => id);
      getPermissionedUsers({ contactIds });
    }
  }, [contacts]);

  const handleRoleSelection = (roleTypeId, userId) => {
    const newPermissionedUsersState = permissionedUsersState.map(user => (user.id === userId ?
      determinePermissions(user, roleTypeId) :
      ({ ...user })));

    setPermissionedUsersState(newPermissionedUsersState);
  };

  const determinePermissions = (user, roleTypeId) => (roleTypeId === LrdRoleType.ADMIN ? ({ ...user, isApprover: true, isPrivileged: true, lenderReportingRoleTypeId: roleTypeId }) : { ...user, lenderReportingRoleTypeId: roleTypeId });

  const handleCheck = (checkType, userId) => {
    const newPermissionedUsersState = permissionedUsersState.map(user => (user.id === userId ?
      ({
        ...user,
        isApprover: checkType === LrdCheckType.APPROVER ? !user?.isApprover : user?.isApprover,
        isPrivileged: checkType === LrdCheckType.PRIVILEGED ? !user?.isPrivileged : user?.isPrivileged
      }) :
      { ...user }));

    setPermissionedUsersState(newPermissionedUsersState);
  };

  const handleDeleteRow = userId => {
    const newPermissionedUsersState = permissionedUsersState.filter(({ id }) => id !== userId);
    if (userId) {
      setDeletedUserIds([...deletedUserIds, userId]);
    }
    setPermissionedUsersState(newPermissionedUsersState);
    resetUserOptions(newPermissionedUsersState);
  };

  const handleAddRow = contactId => {
    const foundContact = flatContacts?.find(contact => contact.id === contactId);
    const newRow = {
      id: uuidv4(),
      contactId,
      lenderReportingRoleTypeId: LrdRoleType.USER,
      isPrivileged: false,
      isApprover: false,
      contact: foundContact
    };

    setPermissionedUsersState([...permissionedUsersState, newRow]);
  };

  const resetUserOptions = permissionedUsersState => {
    const userOptions = flatContacts.map(({ name, id }) => ({ name, id }));
    const contactIds = permissionedUsersState.map(({ contactId }) => contactId);
    setContactsState(userOptions.filter(c => !contactIds.some(ci => ci === c.id)));
  };

  return (
    <PageContainer>
      {(isSystemAdmin || isLrdAdmin) ?
        <>
          <SettingsPageHeader
            deletedUserIds={deletedUserIds}
            originalPermissionedUsers={originalPermissionedUsers}
            setOriginalPermissionedUsers={setOriginalPermissionedUsers}
            permissionedUsersState={permissionedUsersState}
            setPermissionedUsersState={setPermissionedUsersState}
            updatePermissionedUsers={updatePermissionedUsers}
          />
          <SettingsModule
            allUsers={contactsState}
            handleAddRow={handleAddRow}
            handleCheck={handleCheck}
            handleDeleteRow={handleDeleteRow}
            handleRoleSelection={handleRoleSelection}
            permissionedUsersState={permissionedUsersState}
            selectedUser={selectedUser}
            setSelectedUser={setSelectedUser}
            setPermissionedUsersState={setPermissionedUsersState}
          />
        </> :
        <UnauthorizedSection>
          <div>You are not authorized to see lender reporting settings.</div>
          <div>
            Go back to
            <span style={{ color: "#005AA9", cursor: "pointer" }} onClick={() => history.push(LENDER_REPORTING_DASHBOARD)}>Lender Compliance Reporting Dashboard</span>
            .
          </div>
        </UnauthorizedSection>}
    </PageContainer>
  );
};

const mapStateToProps = ({ contacts, lenderReportingDashboard, authentication }) => ({
  ...lenderReportingDashboard,
  authentication,
  contacts: contacts.contactsByCompany
});

export default compose(
  withRouter,
  connect(
    mapStateToProps, {
      getContactsByCompany,
      getPermissionedUser,
      getPermissionedUsers,
      updatePermissionedUsers
    }
  )
)(LenderReportingSettings);
